import { useCallback } from 'react';
import { type RoutePath, type RouteSegments, useRouter, useSegments } from 'expo-router';
import { Platform } from 'react-native';

import { useLastInteractedStoreMachine } from '../../context/LastInteractedStore/useLastInteractedStoreMachine';
import { useResetMenuStack } from '../../routing/hooks/useResetMenuStack';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a deferred cleanup function that preserves the last placed-order location
 * while clearing group-order location state, stale Menu navigation, and customization UI.
 */
export const useResetGroupOrderingState = () => {
  const router = useRouter();
  const segments = useSegments();

  const { send } = useLastInteractedStoreMachine();
  const resetMenuStack = useResetMenuStack();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isCustomizationScreen = segments.some((segment) => segment === CUSTOMIZATION_SEGMENT);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const performReset = useCallback(() => {
    send({ type: 'RESET_ACTIVE_CART_STORE' });
    send({ type: 'RESET_LAST_INTERACTED_MENU_STORE' });

    resetMenuStack({ screenName: MENU_SCREEN });

    if (isCustomizationScreen) {
      router.dismissAll();
    }
  }, [isCustomizationScreen, resetMenuStack, router, send]);

  const resetGroupOrderingState = useCallback(() => {
    if (Platform.OS === 'web') {
      setTimeout(performReset);

      return;
    }

    requestIdleCallback(performReset);
  }, [performReset]);

  return resetGroupOrderingState;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CUSTOMIZATION_SEGMENT = '(customization)' satisfies RouteSegment;
const MENU_SCREEN = 'menu/index' satisfies StackRouteName<RoutePath>;

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteSegment = RouteSegments<RoutePath>[number];

type StackRouteName<Route extends RoutePath> = Route extends `/${infer Path}`
  ? `${Path}/index`
  : never;
